/**
 * PKCE (Proof Key for Code Exchange) utility functions
 * Implements RFC 7636 for OAuth 2.0 Authorization Code Flow with PKCE
 */

import { sha256 } from 'js-sha256';

/**
 * Check if Web Crypto API is available
 * @returns true if crypto.subtle is available
 */
function isCryptoAvailable(): boolean {
  return typeof crypto !== 'undefined' && 
         typeof crypto.subtle !== 'undefined' &&
         typeof crypto.subtle.digest === 'function';
}

/**
 * Generate a cryptographically random code verifier
 * @returns Base64URL-encoded string between 43 and 128 characters
 */
export function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(array);
  } else {
    // Fallback for environments without crypto.getRandomValues
    for (let i = 0; i < array.length; i++) {
      array[i] = Math.floor(Math.random() * 256);
    }
  }
  
  return base64URLEncode(array);
}

/**
 * Generate a code challenge from a code verifier using SHA256
 * @param codeVerifier The code verifier string
 * @returns Base64URL-encoded SHA256 hash of the code verifier
 */
export async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  // Check if Web Crypto API is available
  if (!isCryptoAvailable()) {
    console.warn('[PKCE] Web Crypto API not available. Using js-sha256 fallback.');
    // Use js-sha256 library as fallback (produces proper SHA-256 hash)
    const hash = sha256.arrayBuffer(codeVerifier);
    return base64URLEncode(new Uint8Array(hash));
  }
  
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64URLEncode(new Uint8Array(digest));
}

/**
 * Generate a cryptographically random state parameter
 * @returns Random string for CSRF protection (32+ characters)
 */
export function generateState(): string {
  const array = new Uint8Array(24); // 24 bytes = 32 chars in base64url
  
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(array);
  } else {
    // Fallback for environments without crypto.getRandomValues
    for (let i = 0; i < array.length; i++) {
      array[i] = Math.floor(Math.random() * 256);
    }
  }
  
  return base64URLEncode(array);
}

/**
 * Encode array buffer to Base64URL format
 * @param buffer Uint8Array to encode
 * @returns Base64URL-encoded string
 */
function base64URLEncode(buffer: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...buffer));
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * PKCE parameters for OAuth flow
 */
export interface PKCEParams {
  codeVerifier: string;
  codeChallenge: string;
  state: string;
}

/**
 * Generate complete PKCE parameters for OAuth flow
 * @returns Promise resolving to PKCE parameters
 */
export async function generatePKCEParams(): Promise<PKCEParams> {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateState();
  
  return {
    codeVerifier,
    codeChallenge,
    state
  };
}

/**
 * Store PKCE parameters securely for later retrieval
 * @param params PKCE parameters to store
 * @param provider OAuth provider name
 */
export function storePKCEParams(params: PKCEParams, provider: string): void {
  const key = `pkce_${provider}`;
  sessionStorage.setItem(key, JSON.stringify(params));
}

/**
 * Store PKCE parameters by state parameter (for callback handling)
 * @param state The state parameter from OAuth flow
 * @param params PKCE parameters to store
 */
export function storePKCEParamsByState(state: string, params: PKCEParams): void {
  const key = `pkce_state_${state}`;
  sessionStorage.setItem(key, JSON.stringify(params));
}

/**
 * Retrieve and remove PKCE parameters from storage
 * @param provider OAuth provider name
 * @returns PKCE parameters or null if not found
 */
export function retrievePKCEParams(provider: string): PKCEParams | null {
  const key = `pkce_${provider}`;
  const stored = sessionStorage.getItem(key);
  
  if (!stored) {
    return null;
  }
  
  // Remove from storage after retrieval for security
  sessionStorage.removeItem(key);
  
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

/**
 * Retrieve and remove PKCE parameters by state parameter
 * @param state The state parameter from OAuth callback
 * @returns PKCE parameters or null if not found
 */
export function retrievePKCEParamsByState(state: string): PKCEParams | null {
  const key = `pkce_state_${state}`;
  const stored = sessionStorage.getItem(key);
  
  if (!stored) {
    return null;
  }
  
  // Remove from storage after retrieval for security
  sessionStorage.removeItem(key);
  
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}